// Global axios defaults with automatic retry on network/5xx errors.
// Frontend import this file once (in App.js) to enable resilience app-wide.
import axios from 'axios';
import axiosRetry from 'axios-retry';

// ---------------------------------------------------------------------------
// CSRF double-submit cookie interceptor
// ---------------------------------------------------------------------------
// The backend sets a non-HttpOnly `csrf_token` cookie on login / /auth/me.
// We read it here and echo in the `X-CSRF-Token` header on every mutating
// request. Safe methods (GET/HEAD/OPTIONS) are skipped — the backend doesn't
// check them anyway.
function readCookie(name) {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const parts = document.cookie.split(';');
  for (let i = 0; i < parts.length; i += 1) {
    const raw = parts[i].trim();
    if (raw.startsWith(prefix)) return decodeURIComponent(raw.slice(prefix.length));
  }
  return '';
}

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (MUTATING_METHODS.has(method)) {
    const token = readCookie('csrf_token');
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers['X-CSRF-Token'] && !config.headers['x-csrf-token']) {
        config.headers['X-CSRF-Token'] = token;
      }
    }
  }
  return config;
});

// Retry 2 times with exponential backoff on network / timeout / 502 / 503 / 504.
// NOT 4xx (auth errors, validation) — those are intentional and should propagate.
axiosRetry(axios, {
  retries: 2,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // network error or timeout
    if (axiosRetry.isNetworkOrIdempotentRequestError(error)) return true;
    // transient server errors (502, 503, 504)
    const status = error?.response?.status;
    return status === 502 || status === 503 || status === 504;
  },
  onRetry: (count, err, cfg) => {
    if (typeof console !== 'undefined' && console.info) {
      console.info(`[axios-retry] attempt ${count} for ${cfg?.method?.toUpperCase()} ${cfg?.url}: ${err?.message}`);
    }
  },
});
